import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    createTaskStatus,
    getTaskStatusesByHistory,
    updateTaskStatus,
    updateAnswerStatus,
    deleteTaskStatus
} from '../Api/taskStatusApi';

// Async thunks
export const createTaskStatusAsync = createAsyncThunk(
    'taskStatus/createTaskStatus',
    async (taskStatusData) => {
        const response = await createTaskStatus(taskStatusData);
        return response;
    }
);

export const getTaskStatusesByHistoryAsync = createAsyncThunk(
    'taskStatus/getTaskStatusesByHistory',
    async (historyId) => {
        const response = await getTaskStatusesByHistory(historyId);
        return response;
    }
);

export const updateTaskStatusAsync = createAsyncThunk(
    'taskStatus/updateTaskStatus',
    async (taskStatusData) => {
        const response = await updateTaskStatus(taskStatusData);
        return response;
    }
);

export const updateAnswerStatusAsync = createAsyncThunk(
    'taskStatus/updateAnswerStatus',
    async (payload) => {
        const response = await updateAnswerStatus(payload);
        return response;
    }
);

export const deleteTaskStatusAsync = createAsyncThunk(
    'taskStatus/deleteTaskStatus',
    async (taskStatusData, { dispatch }) => {
        const response = await deleteTaskStatus(taskStatusData);
        // Refresh task statuses after deletion if history_id is available
        if (taskStatusData.history_id) {
            dispatch(getTaskStatusesByHistoryAsync(taskStatusData.history_id));
        }
        return response;
    }
);

const initialState = {
    taskStatuses: [],
    status: 'idle',
    error: null,
    currentHistoryId: null
};

const taskStatusSlice = createSlice({
    name: 'taskStatus',
    initialState,
    reducers: {
        setCurrentHistoryId: (state, action) => {
            state.currentHistoryId = action.payload;
        },
        clearTaskStatuses: (state) => {
            state.taskStatuses = [];
            state.currentHistoryId = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Create task status
            .addCase(createTaskStatusAsync.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(createTaskStatusAsync.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Add the new task status to the array
                if (action.payload) {
                    const exists = state.taskStatuses.find(
                        ts => ts.history_id === action.payload.history_id && ts.sq_id === action.payload.sq_id
                    );
                    if (!exists) {
                        state.taskStatuses.push(action.payload);
                    }
                }
            })
            .addCase(createTaskStatusAsync.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })

            // Get task statuses by history
            .addCase(getTaskStatusesByHistoryAsync.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getTaskStatusesByHistoryAsync.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Remove existing statuses for this history
                const historyId = action.meta.arg;
                state.taskStatuses = state.taskStatuses.filter(ts => ts.history_id !== historyId);
                // Add new statuses
                if (action.payload) {
                    state.taskStatuses.push(...action.payload);
                }
            })
            .addCase(getTaskStatusesByHistoryAsync.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })

            // Update task status
            .addCase(updateTaskStatusAsync.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(updateTaskStatusAsync.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Update the local state if the task status exists
                if (action.payload) {
                    const index = state.taskStatuses.findIndex(
                        ts => ts.history_id === action.payload.history_id && ts.sq_id === action.payload.sq_id
                    );
                    if (index !== -1) {
                        state.taskStatuses[index] = action.payload;
                    } else {
                        state.taskStatuses.push(action.payload);
                    }
                }
            })
            .addCase(updateTaskStatusAsync.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })

            // Update answer status (merges answer_status into existing row if present)
            .addCase(updateAnswerStatusAsync.fulfilled, (state, action) => {
                state.status = 'succeeded';
                if (action.payload) {
                    const { history_id, sq_id, answer_status } = action.payload;
                    const index = state.taskStatuses.findIndex(
                        ts => ts.history_id === history_id && parseInt(ts.sq_id) === parseInt(sq_id)
                    );
                    if (index !== -1) {
                        state.taskStatuses[index] = {
                            ...state.taskStatuses[index],
                            answer_status
                        };
                    } else {
                        state.taskStatuses.push({
                            history_id,
                            sq_id,
                            status: false,
                            answer_status
                        });
                    }
                }
            })
            .addCase(updateAnswerStatusAsync.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })

            // Delete task status
            .addCase(deleteTaskStatusAsync.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(deleteTaskStatusAsync.fulfilled, (state) => {
                state.status = 'succeeded';
            })
            .addCase(deleteTaskStatusAsync.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
    }
});

// Export actions
export const { setCurrentHistoryId, clearTaskStatuses } = taskStatusSlice.actions;

// Export selectors
export const selectAllTaskStatuses = (state) => state.taskStatus.taskStatuses;
export const selectTaskStatusesStatus = (state) => state.taskStatus.status;
export const selectTaskStatusesError = (state) => state.taskStatus.error;
export const selectCurrentHistoryId = (state) => state.taskStatus.currentHistoryId;
export const selectTaskStatusBySqId = (state, sq_id, history_id) => 
    state.taskStatus.taskStatuses.find(ts => 
        parseInt(ts.sq_id) === parseInt(sq_id) && ts.history_id === history_id
    );

export default taskStatusSlice.reducer;
